import axios from 'axios';
import {
  actionCreators as sessionActionCreators,
  tokenScopeFromPath
} from '../../ducks/session';
import { actions as sessionActions } from '../../ducks/session';
import { actionCreators as reqActionCreators } from '../../ducks/request-status';
import { actionCreators as modalActionCreators } from '../../ducks/modals';

import { addFormId, formStates, hasFormId } from '../redux-form-middleware';

import Client, {
  hasError,
  LoginResponse,
  normalizePromise,
  RegisterResponse,
  Uploader
} from '../../../wrapped-cube-client';

import { serverErrorCodes as SERROR } from '../../../wrapped-cube-client';
import { createConsentSubscriber } from '../cookie-consent-middleware';
import { apiRoot } from '../../../config';
import { handleJSONApi } from './retrieve-middleware';

export const SHARELINK_PASSWORD_HEADER_MISSING =
  'SHARELINK_PASSWORD_HEADER_MISSING';
export const SHARELINK_PASSWORD_INCORRECT = 'SHARELINK_PASSWORD_INCORRECT';
export const SHARELINK_VERIFICATION_REQUIRED =
  'SHARELINK_VERIFICATION_REQUIRED';
export const SHARE_REQUEST_ACCESS = 'SHARE_REQUEST_ACCESS';
export const SHARE_NO_ACCESS = 'SHARE_NO_ACCESS';
export const SHARELINK_EXPIRED = 'SHARELINK_EXPIRED';

const magicLinkExp = /\/magic-link\/[^/]+/;
const shareLinkExp = /\/share-link\/[^/]+/;
const reviewLinkExp = /\/review(-link)?\/[^/]+/;
const fileRequestExp = /\/file-request\/[^/]+/;

export const createLoginMiddleware = ({ getState, dispatch }) => {
  let consent = false;

  const bc = new BroadcastChannel('CHANNEL:CUBE3/SESSION');
  let verifyAuthReq = null;

  bc.addEventListener('message', (ev) => {
    if (ev.origin === window.location.origin) {
      if (ev.data?.action === sessionActions.LOGOUT) {
        const userId = getState().session.user;
        if (userId === ev.data?.payload.userId) {
          Client.auth
            .getActiveSessions()
            .catch((e) => {
              console.error('Could not get active sessions');
              return null;
            })
            .then((res) => {
              const sessionId = res?.find((s) => s.meta.is_current)?.id;
              if (
                !ev.data?.payload.sessionId ||
                ev.data?.payload.sessionId === sessionId ||
                (userId && !sessionId)
              ) {
                dispatch(sessionActionCreators.reset({ allSessions: false }));

                Uploader.clearStorage();
              }
            });
        }
      }
    }
  });

  createConsentSubscriber(['functional'])(
    () => (consent = true),
    () => {
      consent = false;
      dispatch(sessionActionCreators.forget());
    }
  );

  Client.auth.handleAuthErrors({
    noAuth: () => {
      console.info('NO AUTHORIZATION');
      return dispatch(sessionActionCreators.verifyAuth());
    },
    noAccess: () => {
      console.info('NO ACCESS');
      return dispatch(sessionActionCreators.verifyAuth());
    },
    noBeta: () => {
      return dispatch(sessionActionCreators.logout({ allSessions: false }));
    }
  });

  return (next) => (action) => {
    const { meta, type, payload } = action;

    if (!meta || !meta.apiClient) {
      return next(action);
    } else {
      const params = meta.apiClient;

      const isMagicLink = magicLinkExp.test(window.location.pathname);
      const isShare = shareLinkExp.test(window.location.pathname);
      const isReview = reviewLinkExp.test(window.location.pathname);
      const isFileRequest = fileRequestExp.test(window.location.pathname);

      switch (type) {
        case sessionActions.SET_CURRENT_ACCOUNT:
          Uploader.clearStorage();
          Client.auth.setWorkspaceId(params.workspaceId);
          break;

        //#region Enrolling TOTP
        case sessionActions.ENROLL_TOTP:
          {
            dispatch(reqActionCreators.markInFlight(action));
            Client.auth
              .enrollTotp()
              .then((res) => {
                // Convert the response to a Blob and create a URL for the image
                const blob = new Blob([res.data], { type: 'image/png' });
                const url = URL.createObjectURL(blob);

                // Set the QR code URL as the source for the image
                dispatch(
                  sessionActionCreators.setEnrollCredentials({ qrCode: url })
                );
                dispatch(reqActionCreators.markSuccess(action));
              })
              .catch((err) => {
                dispatch(reqActionCreators.markFailed(action));
                console.error('Error fetching enroll QR code', err);
              });
          }
          break;
        //#region Finishing enrolling
        case sessionActions.ENROLL_TOTP_FINISH:
          {
            dispatch(reqActionCreators.markInFlight(action));
            const code = payload.totp_code;
            Client.auth
              .enrollTotpFinish(code)
              .then((res) => {
                dispatch(reqActionCreators.markSuccess(action));

                const successAction = sessionActionCreators.verifyCodeSuccess();
                // pass request status to form state
                dispatch(
                  hasFormId(action)
                    ? addFormId(action.meta.reduxForm.form, {
                        formState: formStates.SUCCESS
                      })(successAction)
                    : successAction
                );
              })
              .catch((err) => {
                const failedAction = sessionActionCreators.verifyCodeFailed({
                  errors: { _error: err.response.data.errors }
                });
                dispatch(reqActionCreators.markFailed(action));
                // pass request status to form state
                return dispatch(
                  hasFormId(action)
                    ? addFormId(action.meta.reduxForm.form, {
                        formState: formStates.FAILED
                      })(failedAction)
                    : failedAction
                );
              });
          }
          break;
        //#region generate recovery code
        case sessionActions.GENERATE_RECOVERY_CODE:
          {
            // clear cached recovery codes and qr codes
            if (getState().session.recoveryCode) {
              dispatch(sessionActionCreators.clearEnrollCredentials());
            }
            dispatch(reqActionCreators.markInFlight(action));
            Client.auth
              .generateRecoveryCode(payload.totp_code, payload.regen)
              .then((res) => {
                // JSONApi response
                dispatch(reqActionCreators.markSuccess(action));
                // store data to redux state
                dispatch(
                  sessionActionCreators.setEnrollCredentials({
                    recoveryCode: res.data.data.attributes.codes
                  })
                );
                // pass request status to redux form
                const successAction = sessionActionCreators.verifyCodeSuccess();
                dispatch(
                  hasFormId(action)
                    ? addFormId(action.meta.reduxForm.form, {
                        formState: formStates.SUCCESS
                      })(successAction)
                    : successAction
                );
              })
              .catch((err) => {
                const failedAction = sessionActionCreators.verifyCodeFailed({
                  errors: { _error: err.response.data.errors }
                });
                // pass request status to form state
                dispatch(
                  hasFormId(action)
                    ? addFormId(action.meta.reduxForm.form, {
                        formState: formStates.FAILED
                      })(failedAction)
                    : failedAction
                );
                dispatch(reqActionCreators.markFailed(action));
                console.error('Error re-generating recovery code', err);
              });
          }
          break;
        //#region 2FA login
        case sessionActions.TWOFA_LOGIN: {
          const { token, totp_code } = payload;
          if (!token || !totp_code) {
            console.error('Missing 2fa token or otp code');
          }
          dispatch(reqActionCreators.markInFlight(action));
          Client.auth
            .twoFALogin({
              creds: { token, totp_code }
            })
            .then((res) => {
              const successAction = sessionActionCreators.loginSuccess({
                userId: res.data.id_token,
                tokenScope: 'full',
                twoFAToken: undefined
              });
              dispatch(
                hasFormId(action)
                  ? addFormId(action.meta.reduxForm.form, {
                      formState: formStates.SUCCESS
                    })(successAction)
                  : successAction
              );
              dispatch(reqActionCreators.markSuccess(successAction));
              // if in shares, we want to reload window to show the "magic-link" prompt,
              // and verify share again, if successful, should open the link
              if (isShare || isReview || isFileRequest) {
                window.location.reload();
              }
            })
            .catch((err) => {
              let errors = { ...err };
              if (hasError(err, SERROR.OTP_CODE_INVALID)) {
                errors = { _error: SERROR.OTP_CODE_INVALID };
              }
              const failedAction = sessionActionCreators.loginFailed({
                errors: errors
              });

              dispatch(
                hasFormId(action)
                  ? addFormId(action.meta.reduxForm.form, {
                      formState: formStates.FAILED
                    })(failedAction)
                  : failedAction
              );
              dispatch(reqActionCreators.markFailed(action));
            });
          break;
        }

        //#region Login
        case sessionActions.LOGIN:
          // if user is already login, log them out
          if (getState().session.auth) {
            dispatch(sessionActionCreators.reset({ allSessions: false }));
          }

          dispatch(reqActionCreators.markInFlight(action));
          (
            Client.auth.login({
              creds: {
                username: payload.username,
                password: payload.password
              }
            }) as Promise<LoginResponse>
          )
            .then((res) => {
              dispatch(reqActionCreators.markSuccess(action));
              const successAction = sessionActionCreators.loginSuccess({
                userId: res.data.id_token,
                remember: consent && payload.remember,
                tokenScope: 'full',
                twoFAToken:
                  res.data.token_type === 'otp' ? res.data.token : undefined
              });

              return dispatch(
                hasFormId(action)
                  ? addFormId(action.meta.reduxForm.form, {
                      formState: formStates.SUCCESS
                    })(successAction)
                  : successAction
              );
            })
            .catch((err) => {
              let errors;
              switch (true) {
                case hasError(err, SERROR.LOGIN_ERROR):
                  errors = { _error: SERROR.LOGIN_ERROR };
                  break;
                case hasError(err, SERROR.LOGIN_ERROR_BETA):
                  errors = { _error: SERROR.LOGIN_ERROR_BETA };
                  break;
                default:
                  errors = { ...err };
                  break;
              }

              const failedAction = sessionActionCreators.loginFailed({
                errors: errors
              });

              dispatch(
                hasFormId(action)
                  ? addFormId(action.meta.reduxForm.form, {
                      formState: formStates.FAILED
                    })(failedAction)
                  : failedAction
              );
              dispatch(reqActionCreators.markFailed(action));
            });
          break;
        //#region Magic link
        case sessionActions.LOGIN_MAGIC_LINK: {
          Client.auth
            .magicLinkLogin(action.meta.apiClient.email)
            .then((res) => {
              const successAction = sessionActionCreators.loginSuccess({
                userId: res.data.id_token,
                remember: false,
                tokenScope: tokenScopeFromPath(res.data.redirect_uri)
              });

              dispatch(successAction);
            })
            .catch((e) => console.error(e));
          break;
        }

        case sessionActions.ACTIVATE_MAGIC_LINK: {
          Client.auth
            .magicLinkActivate(action.meta.apiClient)
            .then((res) => {
              const startSession = () => {
                Client.auth.sso({ token: res.data.access_token });

                const sessionSuccessAction = sessionActionCreators.loginSuccess(
                  {
                    userId: res.data.id_token,
                    remember: false,
                    tokenScope: tokenScopeFromPath(res.data.redirect_uri)
                  }
                );
                dispatch(sessionSuccessAction);
                return new Promise((res) => setTimeout(res, 500));
              };
              const successAction = sessionActionCreators.magicLinkActivated({
                startSession,
                redirect_uri: res.data.redirect_uri
              });

              dispatch(successAction);
            })
            .catch((err) => {
              const faledAction = sessionActionCreators.magicLinkFailed({
                errors: err.message || 'Magic Link activation failed'
              });

              dispatch(faledAction);
            });
          break;
        }

        case sessionActions.REQUEST_SHARE_ACCESS: {
          Client.auth
            .requestShareAcccess({
              shareToken: action.meta.apiClient.shareToken,
              email: action.meta.apiClient.email,
              message: action.meta.apiClient.message
            })
            .catch((e) => {
              console.info('DEBUG error', e);

              e.response?.status === 409
                ? Promise.resolve()
                : Promise.reject(e);
            });
          break;
        }
        //#region SSO
        // single-sign-on request has already happened in an IFRAME,
        // now we just need to store the response
        case sessionActions.LOGIN_SSO: {
          Client.auth.sso({
            token: action.meta.apiClient.token
          });

          const successAction = sessionActionCreators.loginSuccess({
            userId: action.meta.apiClient.userId,
            remember: consent && action.meta.apiClient.remember,
            tokenScope: 'full'
          });

          dispatch(
            hasFormId(action)
              ? addFormId(action.meta.reduxForm.form, {
                  formState: formStates.SUCCESS
                })(successAction)
              : successAction
          );

          break;
        }
        //#region Token login (for shares)
        case sessionActions.TOKEN_LOGIN:
          dispatch(reqActionCreators.markInFlight(action));
          // first try with token
          Client.auth.setShareToken(payload.token);

          Client.list('share', {
            field: `shares/?filter[token]=${payload.token}`
          })
            .catch((err) => {
              // check password in share-link view
              const passwordError =
                hasError(err, SHARELINK_PASSWORD_HEADER_MISSING) ||
                hasError(err, SHARELINK_PASSWORD_INCORRECT);

              if (passwordError) {
                return Promise.reject(err);
              }

              const userVerficationError =
                hasError(err, SHARELINK_VERIFICATION_REQUIRED) ||
                hasError(err, SHARE_REQUEST_ACCESS) ||
                hasError(err, SHARE_NO_ACCESS);
              if (userVerficationError) {
                return Promise.reject(err);
              }

              // fallback to using auth from logged in  session
              Client.auth.setShareToken(undefined);
              return Client.list('share', {
                field: `shares/?filter[token]=${payload.token}`
              });
            })
            .then((res: any) => {
              handleJSONApi({
                res: res,
                dispatch: dispatch,
                config: {},
                action: action
              });
              dispatch(reqActionCreators.markSuccess(action));
              dispatch(
                sessionActionCreators.setCurrentSharelink(
                  res.data.primary[0].id
                )
              );
            })
            .catch((err) => {
              dispatch(reqActionCreators.markFailed(action, err));
            });

          break;

        case sessionActions.SET_SHARE_PASSWORD:
          Client.auth.setSharePassword(payload.password, payload.token);
          dispatch(
            sessionActionCreators.tokenLogin({
              token: payload.token as string
            })
          );

          break;
        //#region verify auth
        case sessionActions.VERIFY_AUTH: {
          // only verify session if there is one set
          // ignore magiclinks, reviewlinks and filerequests for now
          // those can trigger 401 errors that are not indicative of a closed session, but just a session with a limited scope
          if (
            getState().session.auth &&
            !(isMagicLink || isReview || isFileRequest)
          ) {
            if (!verifyAuthReq) {
              verifyAuthReq = Client.auth.verify();
            }

            verifyAuthReq
              .then((s) => console.info('session ping'))
              .catch((e) => {
                const twoFaToken = getState().session.twoFAToken;
                // don't clear session inside shares, so that we can still open the page for user to enter authentication code
                if (!twoFaToken) {
                  dispatch(sessionActionCreators.reset({ allSessions: false }));
                }
                setTimeout(() => {
                  // inside sharelinks,etc. we want to just reload the window to show the "magic-link" prompt instead of redirecting to the main login view
                  if (isShare || isReview || isFileRequest) {
                    // don't reload window if 2fa is enabled to keep the authentication page open
                    if (!twoFaToken) {
                      window.location.reload();
                    }
                  } else {
                    dispatch(modalActionCreators.openModal('logged_out_alert'));
                  }
                }, 500);
              })
              .finally(() => {
                verifyAuthReq = undefined;
              });
          }
          return next(action);
        }

        case sessionActions.LOGOUT: {
          // if (
          //   // eslint-disable-next-line no-useless-escape
          //   window.location.pathname.match(/\/(?:share|review)(-link)?\/[^/]+/)
          // ) {
          //   // Don't log out users when they visit a sharelink that triggers 401, because that 401 is related to a different session
          //   return;
          // }

          const userId = getState().session?.user;

          if (getState().session.auth) {
            Client.auth
              .getActiveSessions()
              .catch((e) => {
                console.error('Could not get active sessions');
                return null;
              })
              .then((res) => {
                const currentSession = res?.find((s) => s.meta.is_current)?.id;
                const selectedSession = action.payload.id;

                // logout other windows as well
                bc.postMessage({
                  action: sessionActions.LOGOUT,
                  payload: {
                    userId: userId,
                    sessionId: action.payload.allSessions
                      ? null
                      : selectedSession
                      ? selectedSession
                      : currentSession
                  }
                });

                if (!selectedSession || selectedSession === currentSession) {
                  console.log('hello dispatch');
                  dispatch(
                    sessionActionCreators.reset({
                      allSessions: action.payload.allSessions,
                      id:
                        selectedSession === currentSession
                          ? undefined
                          : selectedSession
                    })
                  );
                  Uploader.clearStorage();
                } else if (
                  selectedSession &&
                  selectedSession !== currentSession
                ) {
                  Client.auth
                    .logout(action.payload.allSessions, action.payload.id)
                    .catch((e) => console.error(e));
                }
              });
          }
          return next(action);
        }

        case sessionActions.RESET:
          Client.auth
            .logout(action.payload.allSessions, action.payload.id)
            .catch((e) => console.error(e))
            .then(() => {
              if (!action.payload.id) {
                // Only clear storage when log yourself out
                Client.auth.clearStores();
                localStorage.clear(); // TODO find a way to save tus/uploader resumables data
                Uploader.clearStorage();
              }
            });

          return next(action);

        case sessionActions.REGISTER_ACCOUNT:
          (
            Client.auth.registerAccount({
              ...params
            }) as Promise<RegisterResponse>
          )
            .then((res) => {
              const successAction = reqActionCreators.markSuccess(action);

              return dispatch(
                hasFormId(action)
                  ? addFormId(action.meta.reduxForm.form, {
                      formState: formStates.SUCCESS
                    })(successAction)
                  : successAction
              );
            })
            .catch((err) => {
              let errors;

              switch (true) {
                case hasError(err, SERROR.INVITE_TOKEN_INVALID_ERROR):
                  errors = { _error: SERROR.INVITE_TOKEN_INVALID_ERROR };
                  break;
                // case LOGIN_ERROR_NO_BETA:
                //   errors = { _error: LOGIN_ERROR_BETA };
                //   break;
                default:
                  errors = { ...err };
                  break;
              }

              const failedAction = reqActionCreators.markFailed(action, errors);

              dispatch(
                hasFormId(action)
                  ? addFormId(action.meta.reduxForm.form, {
                      formState: formStates.FAILED
                    })(failedAction)
                  : failedAction
              );
            });
          break;

        case sessionActions.REQUEST_TOKEN_RESET_PASSWORD:
          dispatch(reqActionCreators.markInFlight(action));
          axios
            .post(`${apiRoot}/forgot-password`, action.payload)
            .then((res) => {
              dispatch(reqActionCreators.markSuccess(action));
            })
            .catch((res) => {
              dispatch(reqActionCreators.markFailed(action));
            });
          break;

        case sessionActions.RESET_PASSWORD:
          dispatch(reqActionCreators.markInFlight(action));
          axios
            .put(`${apiRoot}/forgot-password`, action.payload)
            .then((res) => {
              dispatch(reqActionCreators.markSuccess(action));
            })
            .catch((res) => {
              dispatch(reqActionCreators.markFailed(action));
            });
          break;

        case sessionActions.CHANGE_EMAIL:
          dispatch(reqActionCreators.markInFlight(action));
          normalizePromise(
            Client.auth.request({
              method: 'POST',
              url: `/users/${action.meta.apiClient.userId}/update-email`,
              data: {
                email: action.payload.email,
                value: action.payload.email,
                password: action.payload.password
              }
            })
          )
            .then((res) => {
              dispatch(reqActionCreators.markSuccess(action));
            })
            .catch((res) => {
              dispatch(reqActionCreators.markFailed(action, res));
            });
          break;

        case sessionActions.CONFIRM_EMAIL:
          dispatch(reqActionCreators.markInFlight(action));
          normalizePromise(
            axios.request({
              method: 'POST',
              url: `${apiRoot}/confirm-email`,
              data: {
                token: action.payload.token
              }
            })
          )
            .then((res) => {
              dispatch(reqActionCreators.markSuccess(action));
            })
            .catch((res) => {
              dispatch(reqActionCreators.markFailed(action, res));
            });
          break;

        default:
          break;
      }
      return next(action);
    }
  };
};
